import { Chip, Grid, OutlinedInput } from '@mui/material';
import { withStyles } from '@mui/styles';
import React, { useEffect, useRef, useState } from 'react';

import { RoutalPalette } from '@nx-smartmonkey/ui';
import FormattedMessage from '../../react-components/FormattedCustomMessage';
import Text from '../../react-components/Text';
import { Placeholder } from './FormFields.styled';

const styles = () => ({
  editingChip: {
    marginRight: `4px`,
    marginTop: `4px`,
    marginBottom: `4px`,
  },
  chip: {
    marginRight: `4px`,
    marginTop: `4px`,
    marginBottom: `4px`,
    height: `28px`,
    backgroundColor: RoutalPalette.secondary20n,
  },
  children: {
    minHeight: `36px`,
    width: `100%`,
    padding: `8px`,
    cursor: `pointer`,
    border: `1px solid ${RoutalPalette.neutral20}`,
    '&:hover': {
      backgroundColor: RoutalPalette.neutral05,
    },
    '&:focus': {
      border: `1px solid ${RoutalPalette.primary00}`,
      BorderColor: RoutalPalette.primary00,
    },
    '&:active': {
      border: `1px solid ${RoutalPalette.primary00}`,
      BorderColor: RoutalPalette.primary00,
    },
    borderRadius: `5px`,
  },
  noPadding: {
    padding: `4px 8px`,
    height: `26px`,
  },
});

type FormArrayProps = {
  classes: {
    editingChip: string;
    noPadding: string;
    chip: string;
    children: string;
  };
  enabled?: boolean;
  onUpdate?: (...args: any[]) => any;
  label?: string | React.ReactElement;
  inputPlaceholder?: string;
  array?: string[];
};

export const FormArray = withStyles(styles)(
  ({ classes, array, enabled = true, onUpdate, label, inputPlaceholder }: FormArrayProps) => {
    const [currentVal, setCurrentVal] = useState<string | undefined>(undefined);
    const [focused, setFocused] = useState(false);
    const formRef = useRef();

    const handleRemove = (idx: any) => {
      if (array && onUpdate) {
        array.splice(idx, 1);
        onUpdate(array);
      }
    };

    const isRepeated = (current: any) => {
      let flag = false;
      (array ?? []).some((val: any) => {
        flag = flag || current === val;
        return flag;
      });
      return flag;
    };

    const handleFocusedKeyPress = async (event: any) => {
      switch (event.key) {
        case `Enter`:
          if (currentVal && currentVal !== ``) {
            if (!isRepeated(currentVal) && onUpdate) {
              onUpdate([...(array ?? []), currentVal]);
              setCurrentVal(undefined);
            }
          }
          break;
        default:
          break;
      }
    };

    const handleClick = (evt: any) => {
      if (enabled) {
        // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
        if (formRef.current.contains(evt.target)) {
          setFocused(true);
        } else {
          setFocused(false);
        }
      }
    };

    useEffect(() => {
      if (enabled) {
        document.addEventListener(`mousedown`, handleClick);
        return () => {
          document.removeEventListener(`mousedown`, handleClick);
        };
      }
      return undefined;
    });

    return (
      <Grid container direction="column">
        {label && (
          <Text variant="label" style={{ cursor: `default`, marginBottom: `8px` }}>
            {label}
          </Text>
        )}
        {/* @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call. */}
        <Grid
          container
          ref={formRef}
          alignItems="center"
          className={!focused ? classes.children : ``}
          onBlur={(e: any) => {
            if (e.target.tagName === `INPUT`) {
              return;
            }
            setFocused(false);
          }}
          style={{
            cursor: !enabled ? `default` : `inherit`,
            border: !enabled ? `1px solid ${RoutalPalette.neutral20}` : `inherit`,
          }}
        >
          <div>
            {array && array.length > 0 ? (
              array.map((arrayElem: any, idx: any) => (
                <Chip
                  key={arrayElem}
                  className={classes.chip}
                  onDelete={
                    focused
                      ? () => {
                          handleRemove(idx);
                        }
                      : undefined
                  }
                  label={arrayElem}
                />
              ))
            ) : (
              <>
                {!focused && (
                  <Placeholder>
                    <FormattedMessage id="none" />
                  </Placeholder>
                )}
              </>
            )}
          </div>
          {focused && (
            <Grid
              item
              style={{ width: `100%` }}
              key={`render-${(array ?? []).length}`}
              onKeyDown={handleFocusedKeyPress}
            >
              <Grid container alignItems="center" className={classes.editingChip}>
                <OutlinedInput
                  value={currentVal || ``}
                  classes={{ input: classes.noPadding }}
                  autoFocus
                  fullWidth
                  placeholder={inputPlaceholder}
                  onChange={(evt) => {
                    setCurrentVal(evt.target.value);
                  }}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    );
  }
);
